import React, { useEffect, useState, useRef } from "react";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { useDispatch, useSelector } from "react-redux";
import { HTTP_REQUEST_STATUS_CODES } from "constants/constants";
import IntlMessages from "util/IntlMessages";
import { validateAndLoadUser } from "containers/App";
import { updateAuthToken } from "actions";
import AppUtils from "../util/util";
import { sendUserIdentifiedEvent } from "../lib/gtm";

export function useAuthGate({ onValidateUser }) {
  const [errorMessage, setErrorMessage] = useState();
  const [isValidatingUser, setIsValidatingUser] = useState(false);
  const [userData, setUserData] = useState(null);
  const { enableBanner: isInMaintenance, initialCheckComplete } = useSelector(
    (state) => state.ReducerMaintenanceBanner
  );
  const selectedClient = useSelector(
    (state) => state.ReducerClients.selectedClient
  );

  const dispatch = useDispatch();
  const hasIdentifiedUserRef = useRef(false);
  const auth = useAuth();

  const [hasTriedSignin, setHasTriedSignin] = useState(false);

  useEffect(() => {
    // When the token is refreshed, we need to update the token in redux
    return auth.events.addUserLoaded((user) => {
      dispatch(updateAuthToken(user.access_token));
    });
  }, [auth, auth.events, dispatch]);

  useEffect(() => {
    return auth.events.addUserSignedOut(() => {
      auth.removeUser();
      auth.clearStaleState();
      auth.signinRedirect({ redirect_uri: window.location.href });
    });
  }, [auth, auth.events]);

  useEffect(() => {
    if (!auth.error) return;
    console.error(auth.error);
    setErrorMessage(<IntlMessages id="DIC_LOGIN_AUTH_ERROR_MESSAGE" />);
  }, [auth.error]);

  // Auto sign-in
  useEffect(() => {
    if (isInMaintenance) return;
    if (!initialCheckComplete) return;

    if (
      hasAuthParams() ||
      auth.isAuthenticated ||
      auth.activeNavigator ||
      auth.isLoading ||
      hasTriedSignin ||
      auth.error
    ) {
      return;
    }
    void auth.signinRedirect({ redirect_uri: window.location.href });
  }, [auth, hasTriedSignin, initialCheckComplete, isInMaintenance]);

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated || hasTriedSignin) return;
    setHasTriedSignin(true);
    setIsValidatingUser(true);
    validateAndLoadUser()
      .then((result) => {
        if (result.userData === null) {
          if (result.code) {
            setErrorMessage(<IntlMessages id={result.code} />);
            return;
          }
          // these two status codes are handled via our axios interceptors

          if (
            result.responseStatus !==
              HTTP_REQUEST_STATUS_CODES.SERVER_STOPPED &&
            result.responseStatus !== HTTP_REQUEST_STATUS_CODES.UNAUTHORIZED
          ) {
            setErrorMessage(
              <IntlMessages id="DIC_LOGIN_UNABLE_TO_LOAD_USER_INFO_MESSAGE" />
            );
          }
          return;
        }
        onValidateUser(result.userData);
        setUserData(result.userData);
      })
      .finally(() => {
        setIsValidatingUser(false);
      });
  }, [hasTriedSignin, auth.isLoading, auth.isAuthenticated, onValidateUser]);

  useEffect(() => {
    if (!selectedClient?.id) return;
    AppUtils.setModuleConfigData(userData.user, selectedClient);

    if (userData?.user && !hasIdentifiedUserRef.current) {
      sendUserIdentifiedEvent({
        userId: userData.user.id,
        userName: userData.user.user_name,
        userRole: userData.user.role.name,
        companyName: selectedClient.name,
        companyId: selectedClient.id,
      });
      hasIdentifiedUserRef.current = true;
    }
  }, [selectedClient, dispatch, userData]);

  const isLoading =
    !hasTriedSignin || !auth.isAuthenticated || isValidatingUser;

  return { isLoading, errorMessage };
}
